<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import ViewerIcon from './ViewerIcon.vue'

const props = defineProps({
  step: {
    type: Object,
    required: true
  },
  progress: {
    type: String,
    default: ''
  },
  root: {
    type: Object,
    default: null
  },
  softwareRotated: Boolean
})

defineEmits(['next'])

const flightAnchors = ref([])
let layoutFrame = 0
let joystickObserver = null

function joystickElements() {
  return [
    document.querySelector('.virtual-joysticks__side--left .virtual-joystick'),
    document.querySelector('.virtual-joysticks__side--right .virtual-joystick')
  ]
}

function layoutViewport() {
  if (props.softwareRotated && props.root) {
    return {
      width: props.root.clientWidth || window.innerHeight,
      height: props.root.clientHeight || window.innerWidth
    }
  }
  return { width: window.innerWidth, height: window.innerHeight }
}

function toLayoutRect(raw) {
  if (!props.softwareRotated || !props.root) return raw
  const rootRect = props.root.getBoundingClientRect()
  const rootHeight = props.root.clientHeight || window.innerWidth
  const left = raw.top - rootRect.top
  const top = rootHeight - (raw.right - rootRect.left)
  return {
    top,
    left,
    right: left + raw.height,
    bottom: top + raw.width,
    width: raw.height,
    height: raw.width
  }
}

function positionFlightHints() {
  if (props.step.demo !== 'flight') {
    flightAnchors.value = []
    return
  }

  const [leftElement, rightElement] = joystickElements()
  const leftRaw = leftElement?.getBoundingClientRect?.()
  const rightRaw = rightElement?.getBoundingClientRect?.()
  const leftRect = leftRaw ? toLayoutRect(leftRaw) : null
  const rightRect = rightRaw ? toLayoutRect(rightRaw) : null
  const viewport = layoutViewport()
  if (!leftRect?.width || !leftRect?.height) {
    flightAnchors.value = [
      { side: 'left', placement: 'fallback', style: { left: '18%', top: '66%' } },
      rightRect?.width
        ? { side: 'right', placement: 'fallback', style: { left: '82%', top: '66%' } }
        : { side: 'right', placement: 'gesture', style: { left: '74%', top: '54%' } }
    ]
    return
  }

  const bubbleWidth = Math.min(154, Math.max(112, viewport.width * .24))
  if (rightRect?.width && rightRect?.height) {
    const rects = [leftRect, rightRect]
    const middleGap = rightRect.left - leftRect.right
    const placeBeside = viewport.width > viewport.height && middleGap >= bubbleWidth * 2 + 28
    flightAnchors.value = rects.map((rect, index) => {
      const side = index === 0 ? 'left' : 'right'
      if (placeBeside) {
        return {
          side,
          placement: 'beside',
          style: {
            left: `${index === 0 ? rect.right + 14 : rect.left - 14}px`,
            top: `${rect.top + rect.height / 2}px`,
            '--flight-leader-length': `${rect.width / 2 + 14}px`
          }
        }
      }
      return {
        side,
        placement: 'above',
        style: {
          left: `${rect.left + rect.width / 2}px`,
          top: `${rect.top - 12}px`,
          '--flight-leader-length': `${rect.height / 2 + 12}px`
        }
      }
    })
    return
  }

  const roomBeside = viewport.width > viewport.height && viewport.width - leftRect.right >= bubbleWidth + 48
  const movementAnchor = roomBeside
    ? {
        side: 'left',
        placement: 'beside',
        style: {
          left: `${leftRect.right + 14}px`,
          top: `${leftRect.top + leftRect.height / 2}px`,
          '--flight-leader-length': `${leftRect.width / 2 + 14}px`
        }
      }
    : {
        side: 'left',
        placement: 'above',
        style: {
          left: `${leftRect.left + leftRect.width / 2}px`,
          top: `${leftRect.top - 12}px`,
          '--flight-leader-length': `${leftRect.height / 2 + 12}px`
        }
      }
  flightAnchors.value = [
    movementAnchor,
    {
      side: 'right',
      placement: 'gesture',
      style: {
        left: `${viewport.width * .74}px`,
        top: `${viewport.height * .54}px`
      }
    }
  ]
}

function scheduleFlightLayout() {
  cancelAnimationFrame(layoutFrame)
  layoutFrame = requestAnimationFrame(positionFlightHints)
}

function observeJoysticks() {
  joystickObserver?.disconnect()
  if (typeof ResizeObserver === 'undefined') return
  joystickObserver = new ResizeObserver(scheduleFlightLayout)
  joystickElements().forEach(element => {
    if (element) joystickObserver.observe(element)
  })
}

onMounted(() => {
  window.addEventListener('resize', scheduleFlightLayout)
  window.addEventListener('orientationchange', scheduleFlightLayout)
  nextTick(() => {
    observeJoysticks()
    scheduleFlightLayout()
  })
})

watch(() => props.step.demo, () => {
  nextTick(() => {
    observeJoysticks()
    scheduleFlightLayout()
  })
})

watch(() => props.step.hints?.[1]?.side, () => {
  nextTick(() => {
    observeJoysticks()
    scheduleFlightLayout()
  })
})

watch(() => props.softwareRotated, () => {
  nextTick(() => {
    observeJoysticks()
    scheduleFlightLayout()
  })
})

onBeforeUnmount(() => {
  cancelAnimationFrame(layoutFrame)
  joystickObserver?.disconnect()
  window.removeEventListener('resize', scheduleFlightLayout)
  window.removeEventListener('orientationchange', scheduleFlightLayout)
})
</script>

<template>
  <div class="canvas-guide" :class="`is-${step.demo}`" @click.stop="$emit('next')">
    <div class="canvas-guide__shade" aria-hidden="true"></div>

    <header class="canvas-guide__header">
      <div class="canvas-guide__heading">
        <span>{{ progress }}</span>
        <h2>{{ step.title }}</h2>
        <p>{{ step.description }}</p>
      </div>
    </header>

    <div class="canvas-guide__stage">
      <div v-if="step.demo === 'orbit'" class="canvas-guide__gestures is-orbit" role="list">
        <div v-for="hint in step.hints" :key="hint.title" class="canvas-guide__gesture" role="listitem">
          <span class="coach-tour__motion canvas-guide__motion" :class="`motion--${hint.motion}`" aria-hidden="true">
            <ViewerIcon :name="hint.icon" />
          </span>
          <span class="canvas-guide__copy">
            <strong>{{ hint.title }}</strong>
            <small>{{ hint.text }}</small>
          </span>
        </div>
      </div>
      <div v-else-if="step.demo === 'flight'" class="canvas-guide__flight-hints" role="list">
        <div
          v-for="(hint, hintIndex) in step.hints"
          :key="hint.title"
          class="canvas-guide__flight-hint"
          :class="[
            `is-${flightAnchors[hintIndex]?.side || (hintIndex === 0 ? 'left' : 'right')}`,
            `is-${flightAnchors[hintIndex]?.placement || 'fallback'}`
          ]"
          :style="flightAnchors[hintIndex]?.style"
          role="listitem"
        >
          <span>{{ hint.side || (hintIndex === 0 ? '左侧摇杆' : '右侧画面') }}</span>
          <strong>{{ hint.title }}</strong>
          <small>{{ hint.text }}</small>
        </div>
      </div>
    </div>

    <footer class="canvas-guide__footer" :class="{ 'is-flight': step.demo === 'flight' }">
      <span>{{ step.demo === 'flight' ? '点击画面继续' : '点击任意位置继续' }}</span>
    </footer>
  </div>
</template>
